// Lib
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash/fp';

// Utils
import { retrieveDeviceId } from '../../../device/deviceService';
import { authenticateWithApple, authenticateWithGoogle } from '../../authUtils';
import { isPlatformIframe } from '../../../platform/utils/platformDetailsUtils';
import { getPlatformDetailsSelector } from '../../../platform/platformSelector';

// Actions
import { login } from '../../authActions';
import { redirectLoggedInUserToWorkspace } from '../../../reducers/navigationActions';

// Components
import FullScreenModal from '../../../components/modal/FullScreenModal';
import Icon from '../../../components/icons/Icon';
import RouteLink from '../../../components/buttons/RouteLink';
import LoginForm from './LoginForm';
import AuthFormDisclaimerSection from '../../AuthFormDisclaimerSection';
import SocialSignInButton from '../../SocialSignInButton';
import AppleLogo from '../../icons/AppleLogo';
import GoogleLogo from '../../icons/GoogleLogo';
import { MobileComponentSwitch } from '../../../mobile/utils/MobileComponentSwitch';
import { AuthSheet } from '../splash/AuthSheet';
import { useTranslator } from '../../../components/i18n/useTranslator';

// Constants
import { AUTH_FLOWS } from '../../../../common/utils/authConstants';

// Styles
import '../AuthModal.scss';

const showOverlay = (route) => get(['overlay'], route) || false;
const getRegisterPath = (route) => get(['registerPath'], route) || 'register';

const getIsGuest = (state) => state.getIn(['app', 'currentUser', 'isGuest']);

const Container = MobileComponentSwitch(FullScreenModal, AuthSheet);

// Redux connect methods
const Login = ({ location, close, route }) => {
    const isGuest = useSelector(getIsGuest);
    const platformDetails = useSelector(getPlatformDetailsSelector);
    const translate = useTranslator('auth', 'login');

    const deviceId = useMemo(retrieveDeviceId, []);

    const dispatch = useDispatch();
    const dispatchLogin = ({ email, password, ...rest }) => dispatch(login(email, password, rest));
    const navigateToWorkspace = () => dispatch(redirectLoggedInUserToWorkspace());

    useEffect(() => {
        if (isGuest) return;

        navigateToWorkspace();
    }, []);

    if (isPlatformIframe(platformDetails)) return null;

    return (
        <Container className="AuthModal" overlay={showOverlay(route)} close={close}>
            <div className="logo flex flex-col items-center">
                <Icon name="milanote-logo-medium" />
            </div>

            <div className="heading">
                <h1>{translate('heading', 'Log in to Milanote')}</h1>
            </div>

            <div className="section">
                <LoginForm deviceId={deviceId} location={location} dispatchLogin={dispatchLogin} />
            </div>

            <div className="section">
                <div className="social-buttons">
                    <SocialSignInButton
                        className="MilanoteGoogleSignIn"
                        logo={<GoogleLogo />}
                        onClick={() => authenticateWithGoogle({ flow: AUTH_FLOWS.SIGN_IN, deviceId })}
                    >
                        {translate('social.googleButton', 'Log in with Google')}
                    </SocialSignInButton>

                    <SocialSignInButton
                        className="MilanoteAppleSignIn"
                        logo={<AppleLogo />}
                        onClick={() => authenticateWithApple({ flow: AUTH_FLOWS.SIGN_IN, deviceId })}
                    >
                        {translate('social.appleButton', 'Log in with Apple')}
                    </SocialSignInButton>
                </div>

                <div className="register-instead">
                    {translate('register.text', "Don't have an account?")}{' '}
                    <RouteLink to={`/${getRegisterPath(route)}`} keepParams location={location}>
                        {translate('register.link', 'Sign up now')}
                    </RouteLink>
                </div>
            </div>

            <AuthFormDisclaimerSection />
        </Container>
    );
};

Login.propTypes = {
    route: PropTypes.object,
    location: PropTypes.object,
    close: PropTypes.func,
};

export default Login;
